import React, {useEffect, useMemo, useState} from "react";
import {Badge} from "./Badge.jsx";
import {SortHeader, sortRows} from "./SortButton.jsx";
import {TradeCandlestickChart} from "./TradeCandlestickChart.jsx";
import {formatDateTime, metricClass, signed} from "../format.js";

const initialSort = {field: "closedAt", direction: "desc"};

function valueFor(trade, field) {
    if (field === "openedAt" || field === "closedAt") {
        return trade[field] ? new Date(trade[field]).getTime() : null;
    }
    return trade[field] == null ? null : trade[field];
}

function onSortFor(current, field) {
    return {
        field,
        direction: current.field === field
            ? (current.direction === "asc" ? "desc" : "asc")
            : ["openedAt", "closedAt", "symbol", "strategyRef"].includes(field) ? "asc" : "desc",
    };
}

function fillValueFor(fill, field) {
    if (field === "timestamp") return fill.timestamp ? new Date(fill.timestamp).getTime() : null;
    return fill[field] == null ? null : fill[field];
}

function price(value) {
    if (value == null) return "—";
    return `$${Number(value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}

function preciseMoney(value) {
    if (value == null) return "—";
    return `$${Number(value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}

function tradeDate(value) {
    return value
        ? new Intl.DateTimeFormat(undefined, {month: "short", day: "numeric", year: "numeric", timeZone: "UTC"}).format(new Date(value))
        : "Open";
}

function score(value) {
    return value == null ? "—" : `${(Number(value) * 100).toFixed(0)}%`;
}

function fillRole(fill, trade) {
    const side = String(fill.side || "").toLowerCase();
    if (side === "entry" || side === "exit") return side;
    const entrySide = String(trade.side || "").toLowerCase() === "long" ? "buy" : "sell";
    return side === entrySide ? "entry" : "exit";
}

function filledQuantity(fills, trade) {
    return fills.reduce((total, fill) => total + (fillRole(fill, trade) === "entry" ? Math.abs(Number(fill.quantity) || 0) : 0), 0);
}

function TradeModal({trade, onClose}) {
    const [fillSort, setFillSort] = useState({field: "timestamp", direction: "asc"});
    const fills = useMemo(() => sortRows(trade.fills || [], fillSort, fillValueFor), [fillSort, trade.fills]);
    const onFillSort = (field) => setFillSort((current) => ({
        field,
        direction: current.field === field ? (current.direction === "asc" ? "desc" : "asc") : field === "timestamp" ? "asc" : "desc",
    }));

    return (
        <div className="trade-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div
                className="trade-modal" role="dialog" aria-modal="true"
                aria-labelledby="trade-modal-title" onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="trade-modal-header">
                    <div>
                        <div className="eyebrow">Trade focus</div>
                        <h2 id="trade-modal-title">
                            {trade.symbol}
                            {" "}
                            ·
                            {" "}
                            {trade.strategyRef}
                        </h2>
                        <div className="subtle">
                            {trade.portfolioName || "—"}
                            {" "}
                            ·
                            {" "}
                            {trade.account || "—"}
                        </div>
                    </div>
                    <button
                        className="button secondary trade-modal-close" type="button" onClick={onClose}
                        aria-label="Close trade details"
                    >
                        ×
                    </button>
                </header>
                <div className="trade-modal-body">
                    <div className="trade-focus-grid">
                        <div className="trade-focus-stat">
                            <span>P&L</span>
                            <strong className={metricClass(trade.pnl)}>{signed(trade.pnl)}</strong>
                        </div>
                        <div className="trade-focus-stat">
                            <span>Open / close</span>
                            <strong>
                                {tradeDate(trade.openedAt)}
                                {" "}
                                →
                                {" "}
                                {tradeDate(trade.closedAt)}
                            </strong>
                        </div>
                        <div className="trade-focus-stat">
                            <span>Plan</span>
                            <strong>
                                SL
                                {" "}{price(trade.plannedStopLoss)}
                                {" "}
                                · TP
                                {" "}{price(trade.plannedTakeProfit)}
                            </strong>
                        </div>
                        <div className="trade-focus-stat">
                            <span>Fills</span>
                            <strong>
                                {fills.length}
                                {" "}
                                ·
                                {" "}
                                {filledQuantity(fills, trade)}
                                {" "}
                                shares
                            </strong>
                        </div>
                    </div>

                    <section className="trade-modal-section trade-chart-section">
                        <div className="trade-modal-section-head">
                            <div>
                                <h3>Candlestick chart</h3>
                                <div className="subtle">OHLC candles with execution fills</div>
                            </div>
                            <div className="subtle">
                                {trade.candles?.length || 0}
                                {" "}
                                candles
                            </div>
                        </div>
                        <TradeCandlestickChart trade={trade} fills={fills}/>
                    </section>

                    <section className="trade-modal-section">
                        <div className="trade-modal-section-head">
                            <div>
                                <h3>Fills</h3>
                                <div className="subtle">Execution tape for this trade</div>
                            </div>
                            <Badge value={trade.status}/>
                        </div>
                        {fills.length ? (
                            <div className="table-wrap">
                                <table className="fill-table">
                                    <thead>
                                        <tr>
                                            <SortHeader
                                                label="Time" field="timestamp" sort={fillSort}
                                                onSort={onFillSort}
                                            />
                                            <th>Leg</th>
                                            <SortHeader
                                                label="Side" field="side" sort={fillSort}
                                                onSort={onFillSort}
                                            />
                                            <SortHeader
                                                label="Qty" field="quantity" sort={fillSort}
                                                onSort={onFillSort} numeric
                                            />
                                            <SortHeader
                                                label="Price" field="price" sort={fillSort}
                                                onSort={onFillSort} numeric
                                            />
                                            <SortHeader
                                                label="Fees" field="fees" sort={fillSort}
                                                onSort={onFillSort} numeric
                                            />
                                            <SortHeader
                                                label="Route" field="route" sort={fillSort}
                                                onSort={onFillSort}
                                            />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fills.map((fill) => {
                                            const role = fillRole(fill, trade);
                                            return (
                                                <tr key={fill.id}>
                                                    <td>{formatDateTime(fill.timestamp)}</td>
                                                    <td><span className={`fill-leg ${role}`}>{role}</span></td>
                                                    <td><Badge value={fill.side}/></td>
                                                    <td className="num">{Math.abs(Number(fill.quantity) || 0).toLocaleString()}</td>
                                                    <td className="num">{price(fill.price)}</td>
                                                    <td className="num">{preciseMoney(fill.fees)}</td>
                                                    <td>{fill.route || "—"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : <div className="empty">No fills for this trade.</div>}
                    </section>

                    <dl className="trade-modal-fields">
                        <div>
                            <dt>Side</dt>
                            <dd>{trade.side || "—"}</dd>
                        </div>
                        <div>
                            <dt>Quantity</dt>
                            <dd>{trade.quantity ?? "—"}</dd>
                        </div>
                        <div>
                            <dt>Score</dt>
                            <dd>{score(trade.score)}</dd>
                        </div>
                        <div>
                            <dt>Entry / exit</dt>
                            <dd>
                                {price(trade.entryPrice)}
                                {" "}
                                /
                                {" "}
                                {price(trade.exitPrice)}
                            </dd>
                        </div>
                        <div>
                            <dt>Placement</dt>
                            <dd className="mono">{trade.placementId || "—"}</dd>
                        </div>
                        <div>
                            <dt>Trade ID</dt>
                            <dd className="mono">{trade.id || "—"}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>
    );
}

export function TradesTable({trades = [], title = "Trades", subtitle, showStrategy = true}) {
    const [sort, setSort] = useState(initialSort);
    const [selected, setSelected] = useState(null);
    const ordered = useMemo(() => sortRows(trades, sort, valueFor), [sort, trades]);
    const onSort = (field) => setSort((current) => onSortFor(current, field));

    useEffect(() => {
        if (!selected) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") setSelected(null);
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [selected]);

    return (
        <section className="section panel trade-review">
            <div className="panel-head section-head">
                <div>
                    <h2>{title}</h2>
                    {subtitle ? <div className="subtle">{subtitle}</div> : null}
                </div>
                <div className="subtle">
                    {trades.length}
                    {" "}
                    records
                </div>
            </div>
            {trades.length ? (
                <div className="table-wrap">
                    <table className="trade-table">
                        <thead>
                            <tr>
                                <SortHeader
                                    label="Date" field="closedAt" sort={sort}
                                    onSort={onSort}
                                />
                                <SortHeader
                                    label="Symbol" field="symbol" sort={sort}
                                    onSort={onSort}
                                />
                                {showStrategy ? (
                                    <SortHeader
                                        label="Strategy" field="strategyRef" sort={sort}
                                        onSort={onSort}
                                    />
                                ) : null}
                                <SortHeader
                                    label="P&L" field="pnl" sort={sort}
                                    onSort={onSort} numeric
                                />
                                <th className="num">Qty</th>
                                <th className="num">Entry / exit</th>
                                <th className="num">SL / TP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordered.map((trade) => (
                                <tr
                                    className={`clickable ${selected?.id === trade.id ? "trade-selected" : ""}`}
                                    key={trade.id}
                                    onClick={() => setSelected(trade)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            setSelected(trade);
                                        }
                                    }}
                                    tabIndex="0"
                                >
                                    <td>{tradeDate(trade.closedAt || trade.openedAt)}</td>
                                    <td>
                                        <strong className="trade-symbol">{trade.symbol}</strong>
                                        <div className="subtle">{trade.side || "—"}</div>
                                    </td>
                                    {showStrategy ? (
                                        <td>
                                            <span className="mono">{trade.strategyRef}</span>
                                            <div className="subtle">{trade.account || "—"}</div>
                                        </td>
                                    ) : null}
                                    <td className={`num ${metricClass(trade.pnl)}`}>{signed(trade.pnl)}</td>
                                    <td className="num">{trade.quantity ?? "—"}</td>
                                    <td className="num">
                                        {price(trade.entryPrice)}
                                        {" "}
                                        /
                                        {" "}
                                        {price(trade.exitPrice)}
                                    </td>
                                    <td className="num">
                                        {price(trade.plannedStopLoss)}
                                        {" "}
                                        /
                                        {" "}
                                        {price(trade.plannedTakeProfit)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : <div className="empty">No trade-level records are available in this snapshot.</div>}
            {selected ? <TradeModal trade={selected} onClose={() => setSelected(null)}/> : null}
        </section>
    );
}
